/**
 * The UX metric catalog — the single source of truth for every metric the
 * portal emits.
 *
 * Why a catalog instead of inline strings: the metric name is the join key for
 * every chart and alert built on top of it. A free-string call site drifts
 * ("dashview.sort" vs "dashview.sorted") and silently splits one dashboard
 * into two half-populated ones, with nothing failing to signal it. Declaring
 * names here makes a typo a compile error instead.
 *
 * Naming: `portal.<surface>.<object>.<action>`.
 *
 * `attrs` declares the attribute keys a metric may carry. They are enforced at
 * both ends: `MetricAttributes` makes an undeclared key a type error, and the
 * emitter drops one at runtime. That keeps a metric's group-by dimensions
 * stable over time — a call site cannot widen the shape by accident.
 *
 * Attribute VALUES must stay low-cardinality and non-sensitive: enums,
 * booleans, numbers, bucket labels, static route literals. Never patient- or
 * user-authored text. `metrics.ts` holds the runtime guard.
 */

export type MetricKind = "count" | "gauge" | "distribution";

export interface MetricDef<K extends MetricKind = MetricKind> {
  readonly name: string;
  readonly kind: K;
  /** Sentry unit, e.g. "millisecond". Omit for dimensionless counts. */
  readonly unit?: string;
  readonly attrs: readonly string[];
}

/** Attribute keys allowed for a given metric, all optional. */
export type MetricAttributes<D extends MetricDef> = Partial<
  Record<D["attrs"][number], string | number | boolean>
>;

/**
 * Seed set. Deliberately small: three metric kinds across two surfaces, enough
 * to prove ingestion, attributes, group-by and percentiles in prod before we
 * commit to a full catalog.
 */
export const METRICS = {
  /** Does anyone actually use Expand/Collapse All in the MRR tab? */
  MRR_EXPAND_ALL_TOGGLED: {
    name: "portal.mrr.expand_all.toggled",
    kind: "count",
    // action: "expand" | "collapse"
    attrs: ["action"],
  },

  /**
   * Which MRR timeline filters get used, and from where — the evidence for
   * which belong in the bar and which behind "More filters" (#10262 placed
   * them on anecdote).
   *
   * User changes only; deep links and initial state do not count. Search is
   * counted on the empty ↔ non-empty edge rather than per debounced keystroke,
   * so it compares with one-click filters instead of swamping them.
   *
   * Reading the counts: multi-selects (entry_type, file, appointment) emit
   * `applied` on every change that leaves a selection, so they run high
   * against checkboxes — compare `applied` per filter with that in mind. A
   * body-part query cleared by emptying the search box emits `search` /
   * `cleared`, so search `cleared` can exceed its `applied`.
   */
  MRR_TIMELINE_FILTER_USED: {
    name: "portal.mrr.timeline_filter.used",
    kind: "count",
    // filter: see TimelineFilterKind
    // surface: "bar" | "more_filters" | "phone_bar" | "phone_sheet"
    // action: "applied" | "cleared"; mode: "display" | "edit"
    attrs: ["filter", "surface", "action", "mode"],
  },

  /**
   * Opens of the MRR timeline's "More filters" popover or phone filter sheet.
   * The denominator for the hidden filters: an open with no
   * `MRR_TIMELINE_FILTER_USED` after it is someone looking and not finding.
   */
  MRR_TIMELINE_FILTER_PANEL_OPENED: {
    name: "portal.mrr.timeline_filter_panel.opened",
    kind: "count",
    // surface: "more_filters" | "phone_sheet"; mode: "display" | "edit"
    attrs: ["surface", "mode"],
  },

  /**
   * Which dashboard filters get used, and which are dead weight.
   *
   * A user's own change to one filter. "Clear all" and preset toggles reset
   * filters through the same callbacks but are counted as controls in
   * {@link DASHVIEW_FILTER_CONTROL_USED}, not here.
   */
  DASHVIEW_FILTER_CHANGED: {
    name: "portal.dashview.filter.changed",
    kind: "count",
    // dashboard: static route literal; action: "applied" | "cleared"
    // filter: FilterType value (incl. DATE_RANGE, INQUIRY_DATE_RANGE,
    //   DUE_DATE_RANGE), or "checklist:<field>" for the checklist row
    // surface: "main" | "more_filters" (ops-team / checklist row)
    //   | "phone_sheet"
    attrs: ["dashboard", "filter", "action", "surface"],
  },

  /**
   * Dashboard filter controls that act on several filters at once, or reveal
   * them: presets, "Clear all", the "more filters" toggle and the phone sheet.
   * `more_filters_opened` is the denominator for the hidden row's filters.
   */
  DASHVIEW_FILTER_CONTROL_USED: {
    name: "portal.dashview.filter_control.used",
    kind: "count",
    // control: "clear_all" | "preset_applied" | "preset_deselected"
    //   | "preset_created" | "preset_deleted" | "more_filters_opened"
    //   | "filter_sheet_opened"
    attrs: ["dashboard", "control"],
  },

  /**
   * Which sort option is slowest. Sorting is server-side — the sort field
   * selects the GSI — so the cost lives in the query, not on the client. This
   * times the fetch itself.
   */
  DASHVIEW_QUERY_DURATION: {
    name: "portal.dashview.query.duration",
    kind: "distribution",
    unit: "millisecond",
    // server_filter_bucket: filterCountBucket of the server filters the
    //   query sends (always "0" for doctors, whose list query takes none).
    //   Query shape, not user choice: it includes a page's fixed type scope,
    //   so it can exceed the user's `active_bucket`. Date ranges are key
    //   conditions, not filters, and are not counted.
    attrs: [
      "sort_field",
      "sort_direction",
      "is_first_page",
      "server_filter_bucket",
    ],
  },

  /**
   * Round trips needed before a view first shows anything. Post-filters run on
   * the client after the fetch, so a page can come back and be stripped to
   * nothing, forcing more fetches to fill the viewport. That amplification is
   * invisible in query duration alone.
   */
  DASHVIEW_PAGES_TO_FIRST_ROWS: {
    name: "portal.dashview.pages_to_first_rows",
    kind: "distribution",
    // active_bucket: filterCountBucket of the user's active filters
    attrs: ["dashboard", "sort_field", "has_post_filter", "active_bucket"],
  },

  /**
   * How many filters are active at once on a dashboard — one per filter state
   * that loaded (a new query, or a change to the client-side filters). Counts
   * loads, not distinct states: returning to an earlier state counts again.
   * The denominator for {@link DASHVIEW_FILTER_ACTIVE_ON_LOAD}.
   *
   * Counts what the view was loaded with, not clicks: a preset or a URL that
   * sets three filters counts as one 3-filter load here, where
   * `DASHVIEW_FILTER_CHANGED` sees zero or three separate changes.
   */
  DASHVIEW_FILTER_STATE_LOADED: {
    name: "portal.dashview.filter_state.loaded",
    kind: "count",
    // active_bucket: filterCountBucket of the active filters
    attrs: ["dashboard", "active_bucket"],
  },

  /**
   * One per active filter in each {@link DASHVIEW_FILTER_STATE_LOADED} state:
   * which filters a view is actually loaded with, and — grouped by
   * `active_bucket` — which ones travel together.
   */
  DASHVIEW_FILTER_ACTIVE_ON_LOAD: {
    name: "portal.dashview.filter.active_on_load",
    kind: "count",
    // filter: same values as DASHVIEW_FILTER_CHANGED
    attrs: ["dashboard", "filter", "active_bucket"],
  },

  /**
   * The one sort that genuinely runs on the client: REPORT_DUE has no usable
   * server-side ordering (its GSI indexes only the sparse `reportDue`
   * override), so the rows are ordered here instead. `row_bucket` controls for
   * input size, which does drive cost for a real sort.
   */
  DASHVIEW_REPORT_DUE_SORT_DURATION: {
    name: "portal.dashview.report_due_sort.duration",
    kind: "distribution",
    unit: "millisecond",
    attrs: ["dashboard", "row_bucket"],
  },

  /**
   * The second sort that runs on the client: HISTORY_CALL_DATE has no GSI at
   * all, so the pre-visit page orders the loaded rows here.
   *
   * A sibling of the REPORT_DUE metric rather than one metric with a `field`
   * attribute: the two sorts run over different row counts on different
   * dashboards, and folding them together would make either series answer a
   * question about the other. Scoped per the naming rule in this module's
   * README.
   */
  DASHVIEW_HISTORY_CALL_SORT_DURATION: {
    name: "portal.dashview.history_call_sort.duration",
    kind: "distribution",
    unit: "millisecond",
    attrs: ["dashboard", "row_bucket"],
  },

  /**
   * Which transport a medical-record preview actually used.
   *
   * Every record now arrives as a blob, so this is really asking whether
   * prefetch won the race: `cache` means the bytes were already local and the
   * open was instant, `download` means the reviewer waited for the whole file.
   * The `cache` share at each `size_bucket` is what says whether the prefetch
   * caps are set usefully, which is the tuning this metric exists to inform.
   *
   * `blob_direct` is the citation deep-link route, which bypasses the preview
   * routing entirely and so never benefits from the cache.
   */
  PDF_PREVIEW_OPENED: {
    name: "portal.pdf_preview.opened",
    kind: "count",
    // transport: "cache" | "download" | "blob_direct" | "page_images"
    //   ("page_images" is the image viewer, which opens no file at all)
    // size_bucket: see fileSizeBucket
    attrs: ["transport", "size_bucket"],
  },

  /**
   * A record prefetch skipped for exceeding `PREFETCH_MAX_BYTES`.
   *
   * Every skip is a guaranteed cold whole-file download later, so this is the
   * direct count of how much the per-file cap is costing. Grouped by
   * `size_bucket`, it also says where to move the cap: skips clustered just
   * above it argue for a raise, a long tail of multi-gigabyte records does not.
   */
  PDF_PREFETCH_SKIPPED: {
    name: "portal.pdf_prefetch.skipped",
    kind: "count",
    // size_bucket: see fileSizeBucket
    attrs: ["size_bucket"],
  },

  /**
   * Peak bytes downloading concurrently during one prefetch run.
   *
   * The in-flight budget is a memory bound, and this is the only evidence that
   * it holds in practice: transient cost is roughly twice this number, against
   * `PREFETCH_TRANSIENT_MAX_BYTES`. A p99 pinned at the budget means prefetch
   * is saturating it and the window is the throughput limit; a p99 far below
   * means the budget is not the constraint and raising it would buy nothing.
   */
  PDF_PREFETCH_PEAK_INFLIGHT_BYTES: {
    name: "portal.pdf_prefetch.peak_inflight_bytes",
    kind: "distribution",
    unit: "byte",
    attrs: [],
  },

  /**
   * Click to the reader looking at the document — the wait they actually feel.
   *
   * The two paths stop the clock at different events, because "looking at the
   * document" is reached differently. The PDF path stops once the bytes are in
   * hand and parsed, after which the first page follows immediately; the image
   * path has no bytes to wait on, so it stops when the first page image is
   * genuinely on screen. It used to stop when the page *keys* resolved, which
   * is when the viewer learns what to fetch — not a moment the reader has.
   *
   * `transport` is load-bearing rather than decorative: a warm cache and a cold
   * download are different products, and pooling them hides both. `size_bucket`
   * likewise — a slow p95 means nothing without knowing whether it came from
   * gigabyte records or ordinary ones.
   *
   * `page_bucket` is what makes the image path readable at all. Its whole claim
   * is that the wait is flat in document length, and `size_bucket` cannot test
   * that: the file is never fetched on that path, so it is always `unknown`
   * there. Reported on both paths so the two curves can be compared on one
   * axis.
   */
  PDF_PREVIEW_READY_DURATION: {
    name: "portal.pdf_preview.ready.duration",
    kind: "distribution",
    unit: "millisecond",
    attrs: ["transport", "size_bucket", "page_bucket"],
  },

  /**
   * Which viewer a record opened in, and the branch that decided it.
   *
   * Every way of not reaching the image viewer looks identical from the
   * outside — the ordinary PDF viewer — so without `reason` a misrouted record
   * is only diagnosable from a repro. `reason` also carries the alert: a rise
   * in `no_page_images` means ingestion stopped rasterising, and a rise in
   * `size_unknown` means the HEAD is failing.
   *
   * `size_bucket` is the value that drove the decision, so the threshold can be
   * moved from the distribution of what it actually sorted rather than from a
   * guess.
   */
  PDF_PREVIEW_ROUTED: {
    name: "portal.pdf_preview.routed",
    kind: "count",
    // viewer: "pdf" | "page_images"
    // reason: see PreviewRouteReason in ~/common/pdfPreview
    // size_bucket: see fileSizeBucket
    attrs: ["viewer", "reason", "size_bucket"],
  },

  /**
   * A press of any control in the PDF preview's toolbar.
   *
   * Usage, not outcomes: which controls readers actually reach for, and
   * whether that differs between the two viewers — `viewer` is the axis that
   * makes the comparison possible, and the reason this is one metric rather
   * than one per button. A control nobody touches on either path is a
   * candidate for removal; one used far more on page images than on the PDF
   * says the image path is missing something.
   *
   * `switch_viewer` appears here *and* in {@link PDF_VIEWER_SWITCHED}. That is
   * deliberate, and the two must not be summed: this one answers "how much is
   * the toolbar used", the other carries `size_bucket` and answers "where
   * should the routing threshold be" — a question the control bar cannot see
   * the input to.
   */
  PDF_CONTROL_USED: {
    name: "portal.pdf_preview.control_used",
    kind: "count",
    // control: see PDFControl, plus "search" and "switch_viewer" (both
    //   rendered into the bar's slots by the caller)
    // viewer: "pdf" | "page_images" — which renderer was on screen
    attrs: ["control", "viewer"],
  },

  /**
   * A reader trying to select or drag text off a page image, where there is no
   * text layer for the gesture to act on.
   *
   * The demand signal for doing something about it. Image mode is chosen by
   * routing, not by the reader, so this is the only way they can tell us that
   * the thing it took away was the thing they wanted — a swipe across a
   * paragraph is a request for text, made with their hands.
   *
   * `size_bucket` carries the decision, not just the description.
   * {@link INLINE_VIEWER_MAX_BYTES} sits at 2 GB and the bucket boundary is the
   * same number, so the split says whether these readers had a way out at all:
   * below it the toolbar's "Open full PDF" works and the fix is to point at it,
   * at `2GB+` that control is disabled and nothing in the app can serve them.
   *
   * Counts gestures, not people. Sentry has no distinct-count primitive (see
   * the README), so this answers "how often is selection reached for" and
   * cannot answer "how many readers want it" — one reader swiping five
   * paragraphs emits five. Read it as demand intensity, not headcount.
   */
  PDF_SELECTION_BLOCKED: {
    name: "portal.pdf_preview.selection_blocked",
    kind: "count",
    // size_bucket: see fileSizeBucket — "2GB+" is the band with no escape hatch
    attrs: ["size_bucket"],
  },

  /**
   * A reader switching viewers from the toolbar.
   *
   * The routing threshold is one guessed constant, and this is how it stops
   * being guessed: presses of "View as page images" clustered in a size band
   * argue for lowering it, presses of "Open full PDF" for raising it. It is
   * also the escape hatch's usage count — a band where readers routinely
   * override us is a band we are routing wrong.
   */
  PDF_VIEWER_SWITCHED: {
    name: "portal.pdf_preview.viewer_switched",
    kind: "count",
    // to: "pdf" | "page_images" — the viewer the reader asked for
    // size_bucket: see fileSizeBucket
    attrs: ["to", "size_bucket"],
  },

  /**
   * Every "Create appointment" click on the inquiry page, tagged with the
   * status the row was in when it was clicked.
   *
   * The committed bookings are already countable server-side — the
   * `APPOINTMENT.SCHEDULED` event carries `properties.old_status` (see
   * `appointment_event_publisher`), which lands in Snowflake. What that stream
   * cannot see is an attempt that never commits, which is the whole reason
   * this metric exists: `outcome` splits the clicks that booked from the ones
   * that died in validation or were backed out of.
   *
   * `source_status` deliberately carries the raw `AppointmentStatus` value
   * ("INQUIRY_CLOSED"), not the page's display string ("Inquiry closed"), so a
   * breakdown here lines up with `old_status` on the backend event rather than
   * needing a translation table. `"NEW"` means there was no existing row —
   * the page was creating an appointment from scratch.
   *
   * One event per click, never a start/finish pair: `TAB_VIEWED` exists
   * because pair-based tracking needs a perfect finish emit on every code
   * path, and any miss leaves an orphan that corrupts exactly the abandonment
   * rate you wanted. `confirm_required` is therefore terminal *for this
   * metric* — the click handed off to the time-slot modal, and whether that
   * modal eventually booked is answered by the backend event, not here.
   */
  APPOINTMENT_CREATE_ATTEMPTED: {
    name: "portal.appointment.create.attempted",
    kind: "count",
    // source_status: raw AppointmentStatus, or "NEW" when creating fresh;
    // outcome: "scheduled" | "validation_failed" | "confirm_required"
    //        | "not_completed"
    attrs: ["source_status", "outcome"],
  },

  /** Point-in-time size of what a dashboard is rendering. */
  DASHVIEW_ROWS_RENDERED: {
    name: "portal.dashview.rows.rendered",
    kind: "gauge",
    attrs: ["dashboard"],
  },
} as const satisfies Record<string, MetricDef>;

export type MetricKey = keyof typeof METRICS;

/**
 * Bucket a file size into a coarse label, on the boundaries the PDF preview
 * logic actually branches at (see `~/common/pdfPreview`), so a group-by lines
 * up with the decisions being measured rather than cutting across them.
 */
export const fileSizeBucket = (bytes: number | null): string => {
  if (bytes === null) return "unknown";
  if (bytes <= 10_000_000) return "0-10MB";
  if (bytes <= 100_000_000) return "10-100MB";
  if (bytes <= 250_000_000) return "100-250MB";
  // 250 MB and 500 MB are both kept as splits: PREFETCH_MAX_BYTES has sat at
  // each in turn, and dropping either would make series from before and after
  // a raise uncomparable.
  if (bytes <= 500_000_000) return "250-500MB";
  if (bytes <= 1_000_000_000) return "500MB-1GB";
  if (bytes <= 2_000_000_000) return "1-2GB";
  return "2GB+";
};

/**
 * Bucket a document's page count into a coarse label.
 *
 * Split where the two viewers diverge rather than on round numbers: the image
 * path fetches a fixed window of page keys whatever the length, while the PDF
 * path parses the whole page tree, so the interesting comparison is at the
 * lengths where that gap should open up. `null` is a document whose length is
 * not known yet.
 */
export const pageCountBucket = (pages: number | null): string => {
  if (pages === null) return "unknown";
  if (pages <= 50) return "0-50";
  if (pages <= 500) return "51-500";
  if (pages <= 2000) return "501-2000";
  if (pages <= 10000) return "2001-10000";
  return "10000+";
};

/** Bucket a number of simultaneously active filters into a coarse label. */
export const filterCountBucket = (count: number): string =>
  count >= 4 ? "4+" : String(count);

/**
 * Bucket a row count into a coarse label. Keeps `row_bucket` low-cardinality —
 * an exact count would make nearly every series unique and useless to group by.
 */
export const rowBucket = (count: number): string => {
  if (count <= 10) return "0-10";
  if (count <= 50) return "11-50";
  if (count <= 200) return "51-200";
  if (count <= 500) return "201-500";
  return "500+";
};
